import {
    AUTOTRANSLATOR_DIAGNOSTIC_PATH as PATH,
    AUTOTRANSLATOR_DIAGNOSTIC_CAPTURE_MS as CAPTURE_MS,
    AUTOTRANSLATOR_DIAGNOSTIC_CHUNK_BYTES as CHUNK_BYTES,
    type AutotranslatorDiagnosticEvent, type AutotranslatorDiagnosticTrack
} from "../../shared/autotranslatorDiagnostics";
import { AutotranslatorAudioEvidence } from "./autotranslatorAudioEvidence";

export type DiagnosticCapture = {
    note(event: AutotranslatorDiagnosticEvent): void;
    stop(): Promise<void>;
    discard(): void;
};
type Options = {
    raw: MediaStream; processed: MediaStream; debugSessionId: string; mode: string;
    settings: Record<string, unknown>; isCurrent(): boolean;
    onState(state: "recording" | "saving" | "saved" | "limit" | "error" | "unsupported"): void;
    onDiagnostic?(event: string, values: Record<string, string | number | boolean>): void;
};

const diagnosticMimes = (): string[] => typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function" ? [] :
    ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus"]
        .filter(mime => MediaRecorder.isTypeSupported(mime));

export function diagnosticMime(): string | null {
    return diagnosticMimes()[0] ?? null;
}

class EncoderStartupError extends Error {}
const errorName = (error: unknown) => error instanceof Error ? error.name.slice(0, 60) : "Error";

// Taps the existing streams. Never requests another microphone, changes the
// send graph, waits in the caption path, or persists browser audio to disk.
export async function startDiagnosticCapture(options: Options): Promise<DiagnosticCapture | null> {
    const mimes = diagnosticMimes();
    if (!mimes.length) {
        options.onDiagnostic?.("diagnostic_unavailable", { stage: "codec", reason: "no_supported_codec" });
        options.onState("unsupported"); return null;
    }
    for (const mimeType of mimes) {
        if (!options.isCurrent()) return null;
        try { return await startCapture(options, mimeType); }
        catch (error) { if (!(error instanceof EncoderStartupError)) throw error; }
    }
    options.onState("unsupported");
    return null;
}

async function startCapture(options: Options, mimeType: string): Promise<DiagnosticCapture | null> {
    const id = crypto.randomUUID();
    const startedAt = Date.now();
    const controller = new AbortController();
    const request = async (suffix: string, init: RequestInit = {}) => {
        const stage = !suffix ? "cleanup" : suffix === "/events" ? "events" : "chunk";
        try {
            const response = await fetch(`${PATH}/${id}${suffix}`, { ...init, credentials: "same-origin",
                signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15_000)]) });
            if (!response.ok) {
                options.onDiagnostic?.("diagnostic_request_failed", { stage, status: response.status });
                throw new Error(`Diagnostic HTTP ${response.status}`);
            }
            return response;
        } catch (error) {
            if (!(error instanceof Error && error.message.startsWith("Diagnostic HTTP")))
                options.onDiagnostic?.("diagnostic_request_failed", { stage, error: errorName(error) });
            throw error;
        }
    };
    for (let attempt = 1; ; attempt++) {
        let status = 0;
        try {
            const created = await fetch(PATH, { method: "POST", credentials: "same-origin", signal: AbortSignal.timeout(15_000), headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id, debugSessionId: options.debugSessionId, startedAt, mode: options.mode, mimeType, settings: options.settings }) });
            status = created.status;
            if (!created.ok) {
                options.onDiagnostic?.("diagnostic_request_failed", { stage: "create", status, attempt });
                throw new Error(`Diagnostic HTTP ${status}`);
            }
            break;
        } catch (error) {
            if (!status) options.onDiagnostic?.("diagnostic_request_failed", { stage: "create", error: errorName(error), attempt });
            if (attempt >= 2 || status && status !== 429 && status < 500) throw error;
            // Creation is idempotent for this UUID, including when the server
            // accepted a request but its response was lost on mobile data.
            await new Promise(resolve => setTimeout(resolve, 500));
            if (!options.isCurrent()) {
                await request("", { method: "DELETE" }).catch(() => undefined);
                return null;
            }
        }
    }
    if (!options.isCurrent()) {
        await request("", { method: "DELETE" });
        return null;
    }
    const events: AutotranslatorDiagnosticEvent[] = [];
    let failed = false, discarded = false, stopped = false, limited = false;
    let pendingBytes = 0;
    // Independent ordered lanes keep slow event writes from starving both
    // encoders. Each track still uploads contiguous chunks in sequence.
    const uploads = { raw: Promise.resolve(), processed: Promise.resolve(), events: Promise.resolve() };
    let stopPromise: Promise<void> | null = null;
    const recorders: MediaRecorder[] = [];
    const recorderStops: Promise<void>[] = [];
    const uploaded = { raw: 0, processed: 0 };
    const produced = { raw: 0, processed: 0 };
    const evidence = { raw: new AutotranslatorAudioEvidence(mimeType), processed: new AutotranslatorAudioEvidence(mimeType) };
    const lastOffset = { raw: 0, processed: 0 };
    let stoppedAt = 0;
    const startupChecks: Array<{ ready: Promise<void>; cancel(): void }> = [];
    let starting = true;
    let eventTimer: ReturnType<typeof setInterval> | undefined;
    let limitTimer: ReturnType<typeof setTimeout> | undefined;
    const enqueue = (task: () => Promise<void>, size = 0, lane: keyof typeof uploads = "events") => {
        pendingBytes += size;
        // Slow/offline uploads must not grow an unbounded RAM recording.
        if (pendingBytes > 2 * 1024 * 1024) { fail("upload_backlog"); return; }
        uploads[lane] = uploads[lane].then(async () => {
            try { if (!failed && !discarded) await task(); }
            finally { pendingBytes -= size; }
        }).catch(() => fail("upload"));
    };
    const flushEvents = () => {
        const batch: AutotranslatorDiagnosticEvent[] = [];
        let budget = 0;
        while (events.length && batch.length < 50) {
            const size = new TextEncoder().encode(JSON.stringify(events[0])).length;
            if (batch.length && budget + size > 70_000) break;
            batch.push(events.shift()!); budget += size;
        }
        if (!batch.length) return;
        const body = JSON.stringify({ events: batch });
        enqueue(async () => { await request("/events", { method: "POST", headers: { "Content-Type": "application/json" }, body }); }, body.length * 2);
    };
    const stop = (): Promise<void> => {
        if (stopPromise) return stopPromise;
        stopped = true;
        stoppedAt = Date.now();
        clearInterval(eventTimer); clearTimeout(limitTimer);
        if (!failed && !discarded) options.onState("saving");
        let encoderDeadline: ReturnType<typeof setTimeout>;
        const encodersFinished = Promise.race([
            Promise.all(recorderStops),
            new Promise<void>(resolve => { encoderDeadline = setTimeout(() => { fail("encoder_stop_timeout"); resolve(); }, 2000); })
        ]).finally(() => clearTimeout(encoderDeadline));
        // Assign before stop(): a synchronous final blob may hit the RAM cap
        // and call fail(), which re-enters stop().
        stopPromise = encodersFinished.then(async () => {
            while (events.length) flushEvents();
            await Promise.all(Object.values(uploads));
            if (discarded) return;
            const complete = uploaded.raw > 0 && uploaded.processed > 0 &&
                uploaded.raw === produced.raw && uploaded.processed === produced.processed;
            if (!failed && !complete) fail(!uploaded.raw || !uploaded.processed ? "empty_track" : "incomplete_tracks");
            if (!failed && (!evidence.raw.valid || !evidence.processed.valid)) fail("invalid_track");
            const values = { rawChunks: uploaded.raw, processedChunks: uploaded.processed,
                rawProduced: produced.raw, processedProduced: produced.processed,
                rawBytes: evidence.raw.bytes, processedBytes: evidence.processed.bytes,
                rawMedia: evidence.raw.valid, processedMedia: evidence.processed.valid,
                rawLastOffsetMs: lastOffset.raw, processedLastOffsetMs: lastOffset.processed,
                capturedMs: stoppedAt - startedAt, limited, complete: complete && !failed };
            // A failed audio lane still needs a bounded final receipt describing
            // the partial capture. Do not use its aborted controller here.
            try {
                const response = await fetch(`${PATH}/${id}/events`, { method: "POST", credentials: "same-origin",
                    signal: AbortSignal.timeout(15_000), headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ stoppedAt, events: [{ time: stoppedAt, sequence: 0,
                        event: "diagnostic_capture_summary", detail: Object.entries(values).map(([key, value]) => `${key}=${value}`).join(" ") }] }) });
                if (!response.ok) throw new Error(`Diagnostic HTTP ${response.status}`);
            } catch (error) {
                options.onDiagnostic?.("diagnostic_request_failed", { stage: "finalize", error: errorName(error) });
                fail("finalize");
            }
            if (!discarded && !failed) {
                options.onDiagnostic?.("diagnostic_capture_saved", values);
                options.onState(limited ? "limit" : "saved");
            }
        });
        for (const recorder of recorders) if (recorder.state !== "inactive") recorder.stop();
        return stopPromise;
    };
    function fail(reason: string) {
        if (failed || discarded) return;
        failed = true;
        controller.abort();
        options.onDiagnostic?.("diagnostic_capture_failed", { reason, mime: mimeType, rawChunks: uploaded.raw, processedChunks: uploaded.processed,
            rawBytes: evidence.raw.bytes, processedBytes: evidence.processed.bytes, rawMedia: evidence.raw.valid, processedMedia: evidence.processed.valid });
        options.onState("error");
        void stop();
    }
    try {
        for (const track of ["raw", "processed"] as AutotranslatorDiagnosticTrack[]) {
            const recorder = new MediaRecorder(options[track], { mimeType, audioBitsPerSecond: 48_000 });
            recorders.push(recorder);
            let stoppedEncoder!: () => void;
            const encoderStop = new Promise<void>(resolve => { stoppedEncoder = resolve; recorder.addEventListener("stop", () => resolve(), { once: true }); });
            let startupTimer: ReturnType<typeof setTimeout>;
            const ready = new Promise<void>((resolve, reject) => {
                startupTimer = setTimeout(() => reject(new DOMException("Encoder did not start", "TimeoutError")), 8000);
                recorder.addEventListener("start", () => { clearTimeout(startupTimer); resolve(); }, { once: true });
                recorder.addEventListener("error", () => { clearTimeout(startupTimer); reject(new Error("Encoder startup failed")); }, { once: true });
            });
            // Handle a synchronous second-encoder failure without leaving the
            // first encoder's pending start promise or timeout behind.
            void ready.catch(() => undefined);
            startupChecks.push({ ready, cancel: () => clearTimeout(startupTimer) });
            let index = 0;
            recorder.addEventListener("start", () => events.push({ time: Date.now(), sequence: 0, event: "diagnostic_track_started",
                detail: `track=${track} mime=${recorder.mimeType} bitrate=${recorder.audioBitsPerSecond}` }));
            recorder.addEventListener("error", () => { if (!starting) fail("encoder"); });
            recorder.addEventListener("dataavailable", event => {
                if (!event.data.size || discarded || failed) return;
                if (event.data.size > CHUNK_BYTES) { fail("chunk_size"); return; }
                const chunk = index++;
                const offset = Date.now() - startedAt;
                // Suspended mobile timers can fire late. Drop a late blob rather
                // than retain audio beyond the advertised recording window.
                if (offset > CAPTURE_MS + 1000) { limited = true; void stop(); return; }
                produced[track]++;
                lastOffset[track] = offset;
                enqueue(async () => {
                    await evidence[track].inspect(event.data);
                    // Keep contiguous MediaRecorder chunks: only their concatenation
                    // is a complete playable file, including the first header.
                    for (let attempt = 0; ; attempt++) {
                        try { await request(`/${track}/${chunk}`, { method: "PUT", headers: { "Content-Type": mimeType,
                            "X-Audio-Offset-Ms": String(offset) }, body: event.data });
                            uploaded[track]++;
                            if (chunk === 0) options.onDiagnostic?.("diagnostic_track_uploaded", { track, bytes: event.data.size, mime: mimeType });
                            break; }
                        catch (error) { if (attempt >= 1 || controller.signal.aborted) throw error; }
                    }
                }, event.data.size, track);
            });
            try { recorder.start(5000); recorderStops.push(encoderStop); }
            catch (error) { stoppedEncoder(); throw error; }
        }
        await Promise.all(startupChecks.map(check => check.ready));
        starting = false;
        if (!options.isCurrent()) { discarded = true; await stop(); await request("", { method: "DELETE" }); return null; }
    } catch (error) {
        // Stop any recorder already started if the second track is unsupported.
        failed = true;
        starting = false;
        startupChecks.forEach(check => check.cancel());
        for (const recorder of recorders) if (recorder.state !== "inactive") recorder.stop();
        await Promise.all(recorderStops);
        await Promise.all(Object.values(uploads));
        await request("", { method: "DELETE" });
        options.onDiagnostic?.("diagnostic_codec_failed", { mime: mimeType, error: errorName(error) });
        throw new EncoderStartupError("Diagnostic encoder could not start");
    }
    eventTimer = setInterval(() => flushEvents(), 5000);
    limitTimer = setTimeout(() => { limited = true; void stop(); }, Math.max(0, CAPTURE_MS - (Date.now() - startedAt)));
    options.onState("recording");
    return {
        note(event) {
            if (!stopped && !failed && !discarded) {
                events.push({ ...event, event: event.event.slice(0, 80), detail: event.detail.slice(0, 1200) });
                if (events.length >= 50) flushEvents();
            }
        },
        stop,
        discard() { discarded = true; controller.abort(); events.length = 0; void stop(); }
    };
}
